import Octicon, { Plus } from '@githubprimer/octicons-react';
import * as React from 'react';
import { WithTestIdOptional } from 'shared-client/types/test';
import { handleAxiosErrorWithValidation } from 'util/Misc';
import AutoSuggestContext, { AutoSuggestContextValue } from './AutoSuggestContext';
import { AutoSuggestDataRenderProps } from './AutoSuggestData';
import AutoSuggestNoResultsText from './AutoSuggestNoResultsText';
import { AutoSuggestItem, MultiControllerStateAndHelpers } from './Types';

export interface AutoSuggestMenuProps extends WithTestIdOptional {
	data: AutoSuggestDataRenderProps;
	onCreateClick: () => Promise<void>;
}

interface AutoSuggestMenuState {
	hasError?: boolean;
	errorMessage?: string;
}

/**
 * Renders the list of results from the AutoSuggest server call.
 *
 * @class AutoSuggestMenu
 * @extends {React.Component<AutoSuggestMenuProps>}
 */
class AutoSuggestMenu extends React.Component<AutoSuggestMenuProps, AutoSuggestMenuState> {
	public static contextType: React.Context<any> = AutoSuggestContext;
	public declare context: AutoSuggestContextValue;

	public mounted: boolean = false;

	/**
	 * Local click handlers to handle possible errors from the server.
	 *
	 * @private
	 * @memberof AutoSuggestBase
	 */
	private handleCreateClick = async (): Promise<void> => {
		const { onCreateClick } = this.props;

		this.setState({
			hasError: false,
		});

		try {
			await onCreateClick();
		} catch (error) {
			if (!this.mounted) {
				return;
			}

			const errorInfo = handleAxiosErrorWithValidation(error);

			this.setState({
				hasError: true,
				errorMessage: errorInfo['value'],
			});
		}
	};

	public componentDidMount() {
		this.mounted = true;
	}

	public componentWillUnmount() {
		this.mounted = false;
	}

	// Overriding the default behavior of getA11yStatusMessage to allow for correct implementation
	public getA11yStatusMessage({ isOpen, resultCount }) {
		if (!isOpen) {
			return '';
		}

		if (!resultCount) {
			return '';
		}

		if (resultCount > 0) {
			return `${resultCount} result${
				resultCount === 1 ? ' is' : 's are'
			} available, use up and down arrow keys to navigate. Press Enter key to select.`;
		}

		return '';
	}

	public render() {
		const {
			data: { results, hasMore, isLoading },
			testId,
		} = this.props;

		const {
			renderMenuItem,
			downshift: { getMenuProps, getItemProps, inputValue, highlightedIndex, isOpen },
			autoSuggestOptions: { allowNewValues },
		} = this.context;

		// unfortunately we need to cast here as we can't statically determine if we're doing single vs multiple.
		// this check is used to avoid a scenario where you have multiple values, and you've already selected for example
		// value "Test" - if there are no results (because you've already selected it), without this check it'll try
		// to create "Test" again, rather than just tell you there are no results.
		const selectedItems = (this.context.downshift as MultiControllerStateAndHelpers<AutoSuggestItem>).selectedItems;
		let inputValueAlreadySelected = false;

		if (inputValue && selectedItems && selectedItems.length > 0) {
			inputValueAlreadySelected =
				selectedItems.findIndex(
					(selectedItem) => selectedItem.label.toLocaleLowerCase() === inputValue.toLocaleLowerCase(),
				) > -1;
		}

		// allow users to create values as long as none of the new results match exactly
		const inputValueNotInResults =
			results &&
			inputValue &&
			results.every(
				(item: AutoSuggestItem) => item.label?.toLocaleLowerCase() !== inputValue.toLocaleLowerCase(),
			);

		const displayCreateItem = !isLoading && allowNewValues && inputValueNotInResults && !inputValueAlreadySelected;
		const hasError = this.state && this.state.hasError;
		const displayChangeCultureMessage = this.context.autoSuggestOptions.displayChangeCultureMessage;

		return (
			<>
				{isOpen ? (
					<div className="visually-hidden" role="status" aria-live="polite" aria-relevant="additions text">
						{this.getA11yStatusMessage({
							isOpen,
							resultCount: results.length,
						})}
					</div>
				) : null}

				<div className="autosuggest-menu">
					<ul
						className="dropdown-menu autosuggest-menu__list"
						{...getMenuProps({
							open: true,
						})}
					>
						{hasError ? (
							<li className="autosuggest-menu__item autosuggest-menu__item--error">
								Error: {this.state.errorMessage}
							</li>
						) : null}
						{results &&
							results.map((item: AutoSuggestItem, index) => (
								// tslint:disable-next-line: jsx-key
								<li
									key={index}
									className={
										highlightedIndex === index
											? 'autosuggest-menu__item autosuggest-menu__item--highlighted'
											: 'autosuggest-menu__item'
									}
									{...getItemProps({
										item,
										index,
										key: `${item.value}-${index}`,
									})}
									data-testid={testId ? `${testId}-result-${index}` : `autosuggest-result-${index}`}
								>
									{renderMenuItem({ item })}
								</li>
							))}
						{isLoading ? <li className="autosuggest-menu__item">Loading...</li> : null}
						{!isLoading && hasMore ? (
							<li className="autosuggest-menu__item">
								<em>More results available. Enter keyword to search...</em>
							</li>
						) : null}
						{!isLoading && results && results.length === 0 ? (
							<li className="autosuggest-menu__item">
								<AutoSuggestNoResultsText inputValue={inputValue} />
							</li>
						) : null}
						{displayCreateItem && !hasError ? (
							!displayChangeCultureMessage ? (
								<li className="autosuggest-menu__item" onClick={this.handleCreateClick}>
									<em>
										<Octicon icon={Plus} ariaLabel="Plus sign" /> Create &quot;{inputValue}&quot;
									</em>
								</li>
							) : (
								<li className="autosuggest-menu__item">
									<em>Switch to primary language to create new attribute values</em>
								</li>
							)
						) : null}
					</ul>
				</div>
			</>
		);
	}
}

export default AutoSuggestMenu;
